import React, { useEffect } from 'react';

interface PDFViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl: string;
  documentTitle: string;
}

const PDFViewerModal: React.FC<PDFViewerModalProps> = ({
  isOpen,
  onClose,
  pdfUrl,
  documentTitle
}) => {
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative theme-bg rounded-lg shadow-xl w-[90vw] h-[90vh] max-w-6xl max-h-[90vh] flex flex-col theme-border border">
        {/* Header */}
        <div className="flex items-center justify-between p-4 theme-border border-b">
          <h2 className="text-lg font-semibold theme-text truncate">
            {/* {documentTitle} */}
          </h2>
          <button
            onClick={onClose}
            className="p-2 theme-bg-tertiary hover:theme-bg-tertiary rounded-full transition-colors"
            aria-label="Close PDF viewer"
          >
            <svg 
              className="w-5 h-5 theme-text-secondary" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </button>
        </div>
        
        {/* PDF Content */}
        <div className="flex-1 p-4">
          <iframe
            src={encodeURI(pdfUrl)}
            className="w-full h-full theme-border border rounded"
            title={`PDF Viewer - ${documentTitle}`}
            style={{ minHeight: '500px' }}
          />
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between p-4 theme-border border-t theme-bg-secondary">
          <div className="text-sm theme-text-secondary">
            
          </div>
          <div className="flex space-x-2">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1 text-xs theme-primary theme-text-inverse rounded-xl theme-primary-hover transition-colors"
            >
              Open in New Tab
            </a>
            <button
              onClick={onClose}
              className="px-3 py-1 text-xs theme-secondary theme-text-inverse rounded-xl theme-secondary-hover transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewerModal;
